// src/components/AnalyzeAirbnbModal.js

import React, { useEffect, useState, useCallback } from "react";
import { canUseFeature, incrementUsage } from "../utils/usageLimit";
import { FREE_LIMITS } from "../utils/limits";
import { currencyOptions } from "../utils/formatPrice";
import ProUpgradeDialog from "./ProUpgradeDialog";
import { getAuthHeader } from "../utils/supabaseClient";
import { reportTiming } from "../utils/reportTiming";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Chip,
  Alert,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReviewsIcon from "@mui/icons-material/Reviews";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WifiIcon from "@mui/icons-material/Wifi";
import KitchenIcon from "@mui/icons-material/Kitchen";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import TvIcon from "@mui/icons-material/Tv";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import PoolIcon from "@mui/icons-material/Pool";
import PetsIcon from "@mui/icons-material/Pets";
import HotTubIcon from "@mui/icons-material/HotTub";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import OutdoorGrillIcon from "@mui/icons-material/OutdoorGrill";
import FireplaceIcon from "@mui/icons-material/Fireplace";
import DeckIcon from "@mui/icons-material/Deck";
import WavesIcon from "@mui/icons-material/Waves";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import LocalLaundryServiceIcon from "@mui/icons-material/LocalLaundryService";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import axios from "axios";
import { AMENITIES_MAP } from "../utils/amenitiesMap";
import { AMENITY_EMOJIS } from "../utils/amenitiesMap"; // add at top if not present
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Tooltip from "@mui/material/Tooltip";

const getAmenityIcon = (amenityName) => {
  if (typeof amenityName !== "string")
    return (
      <CheckCircleIcon color="action" sx={{ opacity: 0.6 }} fontSize="small" />
    );
  const lowerName = amenityName.toLowerCase();
  if (lowerName.includes("wifi")) return <WifiIcon fontSize="small" />;
  if (lowerName.includes("kitchen")) return <KitchenIcon fontSize="small" />;
  if (lowerName.includes("air conditioning") || lowerName.includes("ac"))
    return <AcUnitIcon fontSize="small" />;
  if (lowerName.includes("parking"))
    return <LocalParkingIcon fontSize="small" />;
  if (lowerName.includes("pool")) return <PoolIcon fontSize="small" />;
  if (lowerName.includes("tv") || lowerName.includes("television"))
    return <TvIcon fontSize="small" />;
  if (lowerName.includes("pets allowed")) return <PetsIcon fontSize="small" />;
  if (lowerName.includes("hot tub")) return <HotTubIcon fontSize="small" />;
  if (lowerName.includes("gym") || lowerName.includes("exercise equipment"))
    return <FitnessCenterIcon fontSize="small" />;
  if (lowerName.includes("washer") || lowerName.includes("dryer"))
    return <LocalLaundryServiceIcon fontSize="small" />;
  if (lowerName.includes("workspace"))
    return <WorkOutlineIcon fontSize="small" />;
  if (lowerName.includes("fireplace"))
    return <FireplaceIcon fontSize="small" />;
  if (lowerName.includes("patio") || lowerName.includes("balcony"))
    return <DeckIcon fontSize="small" />;
  if (lowerName.includes("bbq") || lowerName.includes("grill"))
    return <OutdoorGrillIcon fontSize="small" />;
  if (lowerName.includes("sauna"))
    return <SelfImprovementIcon fontSize="small" />;
  if (
    lowerName.includes("waterfront") ||
    lowerName.includes("beach access") ||
    lowerName.includes("lake access")
  )
    return <WavesIcon fontSize="small" />;
  if (
    lowerName.includes("self check-in") ||
    lowerName.includes("smart lock") ||
    lowerName.includes("keypad") ||
    lowerName.includes("lockbox")
  )
    return <VpnKeyIcon fontSize="small" />;
  return (
    <CheckCircleIcon color="action" sx={{ opacity: 0.6 }} fontSize="small" />
  );
};

function parseReviewSections(text) {
  if (!text) return null;
  const defs = [
    { label: "What Guests Loved",     color: "success" },
    { label: "What Some Didn't Like", color: "warning" },
    { label: "Red Flags",             color: "error"   },
    { label: "Overall Vibe",          color: "info"    },
  ];
  const chunks = text.split(/\n?##\s+/);
  const result = [];
  for (const chunk of chunks) {
    if (!chunk.trim()) continue;
    const lines = chunk.split("\n");
    const headerLine = lines[0].trim();
    const body = lines.slice(1).join("\n").trim();
    const normalizedHeader = headerLine.replace(/[‘’‚‛]/g, "'");
    const def = defs.find((d) => normalizedHeader.includes(d.label));
    if (!def) continue;
    const bullets = body.split("\n").filter((l) => l.startsWith("- ")).map((l) => l.slice(2).trim()).filter(Boolean);
    const paragraph = bullets.length === 0 ? body : null;
    const emoji = headerLine.replace(def.label, "").trim();
    result.push({ ...def, emoji, bullets, paragraph });
  }
  return result.length > 0 ? result : null;
}

const InteractiveMap = ({ lat, lng, zoom = 13, height = "300px" }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  if (!isLoaded || !lat || !lng) return null;

  return (
    <GoogleMap
      mapContainerStyle={{
        width: "100%",
        height,
        borderRadius: "8px",
      }}
      center={{ lat, lng }}
      zoom={14}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
      }}
    >
      <Marker position={{ lat, lng }} />
    </GoogleMap>
  );
};

const AnalyzeAirbnbModalDashboard = ({
  open,
  onClose,
  listingId,
  price,
  departureDate,
  returnDate,
  deeplink,
  onNext,
  onPrev,
  hasNext,
  hasPrev,
  isPro,
  amenityIds: amenityIdsProp,
  currency,
}) => {
  const [loading, setLoading] = useState(false);
  const [listingData, setListingData] = useState(null);
  const [error, setError] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewStep, setReviewStep] = useState("fetching"); // 'fetching' | 'summarizing'
  const [reviewError, setReviewError] = useState(null);
  const [reviewSummaryData, setReviewSummaryData] = useState(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const CACHE_DURATION_MS = 72 * 60 * 60 * 1000;
  const CACHE_PREFIX = "flybnb_review_cache_";
  const INITIAL_AMENITIES_COUNT = 12;

  const handleAnalyzeReviews = useCallback(
    async (force = false) => {
      if (!listingId) return;
      const cacheKey = `${CACHE_PREFIX}${listingId}`;

      // 1. If not forcing a refresh, try to load from cache first.
      if (!force) {
        try {
          const cachedItem = localStorage.getItem(cacheKey);
          if (cachedItem) {
            const { timestamp, data } = JSON.parse(cachedItem);

            // Check if the cache is still fresh
            if (Date.now() - timestamp < CACHE_DURATION_MS) {
              setReviewSummaryData(data);
              setReviewError(null);
              setReviewLoading(false);
              return; // <-- KEY FIX #1: Exit the function after loading from cache.
            } else {
              // If cache is old, remove it and proceed to fetch new data.
              localStorage.removeItem(cacheKey);
            }
          }
        } catch (cacheError) {
          console.error("Cache read error:", cacheError);
        }
      }

      // 2. Check daily usage limit before making an API call
      if (!canUseFeature("reviews", FREE_LIMITS.reviews, isPro)) {
        setLimitReached(true);
        return;
      }
      incrementUsage("reviews");

      // 3. This code only runs if cache wasn't found, was old, or refresh was forced.
      setReviewLoading(true);
      setReviewStep("fetching");
      setReviewError(null);
      setReviewSummaryData(null);

      // Advance to "summarizing" step after a short delay
      const stepTimer = setTimeout(() => setReviewStep("summarizing"), 3000);

      const backendApiUrl = "https://flybnb.up.railway.app";
      const requestUrl = `${backendApiUrl}/api/reviews/${listingId}`;
      const reviewsT0 = Date.now();
      let reviewsSuccess = false;
      let reviewsErr = null;
      try {
        const response = await fetch(requestUrl, {
          headers: { "Bypass-Tunnel-Reminder": "true", ...(await getAuthHeader()) },
        });
        clearTimeout(stepTimer);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch summary. Status: ${response.status}`
          );
        }
        const data = await response.json();
        setReviewSummaryData(data);
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ timestamp: Date.now(), data })
        );
        reviewsSuccess = true;
      } catch (err) {
        clearTimeout(stepTimer);
        setReviewError(err.message);
        reviewsErr = err?.message;
      } finally {
        setReviewLoading(false);
        reportTiming(
          "reviews",
          Date.now() - reviewsT0,
          reviewsSuccess,
          { listingId },
          reviewsErr
        );
      }
    },
    // KEY FIX #2: The dependency array is corrected to prevent bugs.
    [listingId, CACHE_DURATION_MS, CACHE_PREFIX, isPro]
  );

  useEffect(() => {
    if (!open || !listingId) {
      setListingData(null);
      setError(null);
      setReviewError(null);
      setReviewLoading(false);
      return;
    }
    const fetchListingDetails = async () => {
      setLoading(true);
      setError(null);
      setListingData(null);
      try {
        const response = await axios.request({
          method: "GET",
          url: "https://airbnb13.p.rapidapi.com/room",
          params: { listing_id: listingId, locale: "en", currency: currency || "USD" },
          headers: {
            "x-rapidapi-key": process.env.REACT_APP_RAPIDAPI_KEY,
            "x-rapidapi-host": "airbnb13.p.rapidapi.com",
          },
        });
        if (response.data?.results) {
          setListingData(response.data.results);
          // Auto-trigger review analysis when data is loaded
          handleAnalyzeReviews();
        } else {
          throw new Error("Invalid data structure from Listing API.");
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load details."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchListingDetails();
    setShowAllAmenities(false);
  }, [open, listingId, handleAnalyzeReviews]); // Added handleAnalyzeReviews to the dependency array

  const formatPrice = (price, departureDate, returnDate) => {
    if (!price || !departureDate || !returnDate) return "Price Unavailable";

    const nights = Math.ceil(
      (new Date(returnDate) - new Date(departureDate)) / (1000 * 60 * 60 * 24)
    );

    const nightlyRate = Math.round(price.total / nights);
    const total = Math.round(price.total);

    const formattedNightly = new Intl.NumberFormat("en-US").format(nightlyRate);
    const formattedTotal = new Intl.NumberFormat("en-US").format(total);

    const currencySymbol = currencyOptions.find(c => c.code === (currency || "USD"))?.symbol || "$";
    return `${currencySymbol}${formattedNightly}/night • ${nights} nights • ${currencySymbol}${formattedTotal} total`;
  };

  const mappedAmenities = React.useMemo(() => {
    const ids = listingData?.amenityIds?.length ? listingData.amenityIds : (amenityIdsProp || []);
    if (!ids.length) return [];
    return ids
      .filter((id) => AMENITIES_MAP[id]) // only keep known IDs
      .map((id) => {
        const label = AMENITIES_MAP[id];
        const emoji = AMENITY_EMOJIS[id];
        const name = `${emoji || ""} ${label}`;
        return { id, name, label }; // add label for sorting
      });
  }, [listingData?.amenityIds]);

  // Render the review summary or button based on loading state
  // In the renderReviewSection function, let's update the Paper component styling

  const renderReviewSection = () => {
    if (reviewLoading) {
      const steps = [
        { key: "fetching",    icon: "🔍", label: "Fetching reviews..." },
        { key: "summarizing", icon: "🤖", label: "Running AI analysis..." },
      ];
      return (
        <Box sx={{ py: 2 }}>
          {steps.map((step, i) => {
            const currentIdx = steps.findIndex((s) => s.key === reviewStep);
            const isDone = i < currentIdx;
            const isActive = step.key === reviewStep;
            return (
              <Box
                key={step.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 0.75,
                  opacity: isDone ? 0.45 : isActive ? 1 : 0.25,
                  transition: "opacity 0.4s",
                }}
              >
                {isActive ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>
                    {isDone ? "✅" : step.icon}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{ fontWeight: isActive ? 600 : 400 }}
                >
                  {step.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    } else if (reviewError) {
      return (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {reviewError}
          <Button size="small" sx={{ mt: 1 }} onClick={handleAnalyzeReviews}>
            Retry
          </Button>
        </Alert>
      );
    } else if (reviewSummaryData && reviewSummaryData.summary) {
      const sections = parseReviewSections(reviewSummaryData.summary);
      return (
        <Box sx={{ mt: 1 }}>
          {(reviewSummaryData.count > 0 || listingData?.reviewsCount > 0) && (
            <Typography variant="caption" display="block" sx={{ mb: 1.5, color: "text.secondary" }}>
              Analyzed {reviewSummaryData.count || listingData?.reviewsCount} reviews.
            </Typography>
          )}
          {sections ? (
            <Stack spacing={1.5}>
              {sections.map((section) => {
                const isNone = section.bullets.length === 1 && section.bullets[0].toLowerCase().includes("none");
                return (
                  <Box
                    key={section.label}
                    sx={{
                      borderLeft: "3px solid",
                      borderColor: isNone ? "divider" : `${section.color}.main`,
                      pl: 1.5,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: isNone ? "text.disabled" : `${section.color}.main`,
                        display: "block",
                        mb: 0.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.68rem",
                      }}
                    >
                      {section.emoji} {section.label}
                    </Typography>
                    {section.paragraph ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.85rem",
                          lineHeight: 1.5,
                          color: isNone ? "text.disabled" : "text.primary",
                          fontStyle: section.color === "info" ? "italic" : "normal",
                        }}
                      >
                        {section.paragraph}
                      </Typography>
                    ) : (
                      section.bullets.map((b, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            color: isNone ? "text.disabled" : "text.primary",
                          }}
                        >
                          · {b}
                        </Typography>
                      ))
                    )}
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ p: 1.5, maxHeight: 250, overflow: "auto", borderRadius: 1, bgcolor: "background.paper", borderColor: "divider" }}>
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", lineHeight: 1.6, color: "text.primary" }}>
                {reviewSummaryData.summary}
              </Typography>
            </Paper>
          )}
          <Button size="small" onClick={() => handleAnalyzeReviews(true)} sx={{ mt: 1.5 }} startIcon={<ReviewsIcon />}>
            Refresh Analysis
          </Button>
        </Box>
      );
    } else {
      return (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<ReviewsIcon />}
          onClick={() => handleAnalyzeReviews(true)} // ← force refresh
          sx={{ mt: 1 }}
        >
          Analyze Guest Reviews
        </Button>
      );
    }
  };
  
  const getAugmentedUrl = () => {
    if (!listingData?.url) return "";

    // If dates are missing, just return the basic URL
    if (!departureDate || !returnDate) return listingData.url;

    try {
      // Safe date formatting to YYYY-MM-DD
      const startDate = new Date(departureDate).toISOString().split("T")[0];
      const endDate = new Date(returnDate).toISOString().split("T")[0];

      // Handle existing query params if any
      const separator = listingData.url.includes("?") ? "&" : "?";

      // Return URL with check-in/out params
      return `${listingData.url}${separator}check_in=${startDate}&check_out=${endDate}`;
    } catch (e) {
      // Fallback if date parsing fails
      return listingData.url;
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          {/* Title */}
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: "bold", flexGrow: 1, mr: 2 }}
            noWrap
          >
            {loading && !listingData
              ? "Loading Listing..."
              : listingData?.name ||
                (error ? "Error Loading Listing" : "Listing Analysis")}
          </Typography>

          {/* RIGHT SIDE ACTIONS */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* 1. NAVIGATION — pill on desktop, icon buttons on mobile */}
            {(onNext || onPrev) && (
              isMobile ? (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Previous Listing" arrow>
                    <span>
                      <IconButton size="small" onClick={onPrev} disabled={!hasPrev}>
                        <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next Listing" arrow>
                    <span>
                      <IconButton size="small" onClick={onNext} disabled={!hasNext}>
                        <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "50px",
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[1],
                    overflow: "hidden",
                    height: "36px",
                  }}
                >
                  <Tooltip title="Previous Listing" arrow>
                    <span>
                      <Button
                        onClick={onPrev}
                        disabled={!hasPrev}
                        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "14px !important" }} />}
                        sx={{
                          minWidth: "85px",
                          height: "100%",
                          borderRadius: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "text.primary",
                          pl: 2,
                          pr: 1.5,
                          "&:hover": { backgroundColor: theme.palette.action.hover, color: theme.palette.primary.main },
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        Prev
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ width: "1px", height: "60%", bgcolor: theme.palette.divider }} />
                  <Tooltip title="Next Listing" arrow>
                    <span>
                      <Button
                        onClick={onNext}
                        disabled={!hasNext}
                        endIcon={<ArrowForwardIosIcon sx={{ fontSize: "14px !important" }} />}
                        sx={{
                          minWidth: "85px",
                          height: "100%",
                          borderRadius: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "text.primary",
                          pl: 1.5,
                          pr: 2,
                          "&:hover": { backgroundColor: theme.palette.action.hover, color: theme.palette.primary.main },
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        Next
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )
            )}

            {/* 2. "View on Airbnb" — icon only on mobile */}
            {listingData?.url && (
              <Button
                href={getAugmentedUrl()}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size={isMobile ? "small" : "medium"}
                startIcon={
                  <img
                    src="/logo-airbnb.svg"
                    alt="Airbnb"
                    style={{ width: isMobile ? 16 : 20, height: isMobile ? 16 : 20 }}
                  />
                }
                sx={{
                  backgroundColor: "#FF5A5F",
                  color: "white",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  textTransform: "none",
                  px: isMobile ? 0.75 : 2,
                  py: { xs: 0.5, sm: 0.8 },
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  boxShadow: theme.shadows[2],
                  "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
                  "&:hover": { backgroundColor: "#E14850" },
                }}
                aria-label="View on Airbnb"
              >
                {isMobile ? null : "View on Airbnb"}
              </Button>
            )}

            {/* 3. Close Button */}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <CircularProgress size={50} />
          </Box>
        ) : error ? (
          <Alert severity="error" variant="outlined" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : listingData ? (
          // --- TWO COLUMN LAYOUT ---
          <Grid container spacing={2.5}>
            {/* === LEFT COLUMN (Images & Map) === */}
            <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column" }}>
              <Stack spacing={2.5} sx={{ flex: 1 }}>
                {/* Image Carousel */}
                {listingData.images?.length > 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Carousel
                      showThumbs={false}
                      infiniteLoop
                      useKeyboardArrows
                      autoPlay={false}
                      showStatus={false}
                      emulateTouch
                      dynamicHeight={true}
                      renderArrowPrev={(clickHandler, hasPrev) =>
                        hasPrev && (
                          <IconButton
                            onClick={clickHandler}
                            size="small"
                            sx={{
                              position: "absolute",
                              left: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 2,
                              bgcolor: "rgba(0,0,0,0.55)",
                              color: "white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                            }}
                          >
                            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )
                      }
                      renderArrowNext={(clickHandler, hasNext) =>
                        hasNext && (
                          <IconButton
                            onClick={clickHandler}
                            size="small"
                            sx={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 2,
                              bgcolor: "rgba(0,0,0,0.55)",
                              color: "white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                            }}
                          >
                            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )
                      }
                    >
                      {listingData.images.slice(0, 10).map((src, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "relative",
                            width: "100%",
                            // taller on phones, keep 16:9 on sm+
                            paddingTop: { xs: "72%", sm: "56.25%" },
                            overflow: "hidden",
                            borderRadius: 2,
                            cursor: "pointer",
                          }}
                          onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                        >
                          <img
                            src={src}
                            alt={`${listingData.name || "Image"} ${i + 1}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      ))}
                    </Carousel>
                  </Paper>
                ) : (
                  <Paper
                    elevation={1}
                    sx={{
                      height: { xs: "280px", sm: "350px", md: "420px" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: theme.palette.background.paper,
                      borderRadius: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      No Images Available
                    </Typography>
                  </Paper>
                )}

                {/* Map — desktop only, fills remaining height */}
                <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", flex: 1, minHeight: 320 }}>
                  {listingData.lat && listingData.lng ? (
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Location
                      </Typography>
                      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", borderRadius: 1.5 }}>
                        <InteractiveMap
                          lat={listingData.lat}
                          lng={listingData.lng}
                          height="100%"
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, textAlign: "center", color: "text.secondary" }}
                      >
                        *Approximate location only
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Map data not available.
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Stack>
            </Grid>{" "}
            {/* === END LEFT COLUMN === */}
            {/* === RIGHT COLUMN (Info & Actions) === */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                {" "}
                {/* Stack Info sections vertically */}
                {/* Header Info */}
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant="h6"
                    component="a"
                    href={getAugmentedUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontWeight: "bold",
                      mb: 0.5,
                      lineHeight: 1.3,
                      display: "block",
                      color: "text.primary",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline", color: "primary.main" },
                    }}
                  >
                    {listingData.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {listingData.rating ? (
                      <Box
                        component="a"
                        href={getAugmentedUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mr: 1,
                          textDecoration: "none",
                          color: "inherit",
                          "&:hover span": { textDecoration: "underline" },
                        }}
                      >
                        <StarIcon
                          sx={{ color: "#FFC107", fontSize: "1.1rem", mr: 0.2 }}
                        />
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: "bold", mr: 0.5 }}
                        >
                          {parseFloat(listingData.rating).toFixed(1)}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          color="text.secondary"
                        >
                          ({listingData.reviewsCount ?? 0} reviews)
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mr: 1 }}
                      >
                        No rating yet
                      </Typography>
                    )}
                    <Chip
                      component="a"
                      href={getAugmentedUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<AttachMoneyIcon fontSize="small" />}
                      label={formatPrice(price, departureDate, returnDate)}
                      size="small"
                      variant="outlined"
                      clickable
                      sx={{ "&:hover": { textDecoration: "underline" } }}
                    />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {/* Review Summary or Button */}
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Review Analysis
                    </Typography>
                    {renderReviewSection()}
                  </Box>
                </Paper>
                {/* Map below Review Analysis on phones only */}
                <Box sx={{ mt: 2, display: { xs: "block", md: "none" } }}>
                  {listingData.lat && listingData.lng ? (
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Location
                      </Typography>
                      <Box
                        sx={{
                          maxWidth: "100%",
                          overflow: "hidden",
                          borderRadius: 1.5,
                        }}
                      >
                        <InteractiveMap
                          lat={listingData.lat}
                          lng={listingData.lng}
                          width={600}
                          height={isMobile ? 200 : 250}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mt: 1,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        *Approximate location only
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold" }}
                      >
                        Location
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Map data not available.
                      </Typography>
                    </Paper>
                  )}
                </Box>
                {/* Cancellation Policy */}
                <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    Cancellation Policy
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <InfoOutlinedIcon
                      color="action"
                      sx={{ mr: 1, fontSize: "1rem" }}
                    />
                    <Typography variant="caption">
                      {listingData?.cancelPolicy?.[0] || "Not specified"}
                    </Typography>
                  </Box>
                </Paper>
                {/* Amenities */}
                {mappedAmenities.length > 0 && (
                  <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Amenities
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        justifyContent: "flex-start",
                      }}
                    >
                      {mappedAmenities
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(({ id, name }) => (
                          <Chip
                            key={id}
                            label={name}
                            variant="outlined"
                            size="small"
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? theme.palette.action.selected
                                  : theme.palette.action.hover,
                              fontSize: "0.75rem",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Grid>{" "}
            {/* === END RIGHT COLUMN === */}
          </Grid> // End Two Column Grid
        ) : (
          // Fallback when no data
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              minHeight: "30vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Could not load listing data.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    <ProUpgradeDialog
      open={limitReached}
      onClose={() => setLimitReached(false)}
      feature="Review Analyzer"
      limit={FREE_LIMITS.reviews}
    />

    {/* Lightbox */}
    {lightboxOpen && listingData?.images?.length > 0 && (
      <Box
        onClick={() => setLightboxOpen(false)}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          bgcolor: "rgba(0,0,0,0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* X button */}
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{ position: "absolute", top: 16, right: 16, color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
        >
          <CloseIcon />
        </IconButton>

        {/* Prev arrow */}
        {lightboxIndex > 0 && (
          <IconButton
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
            sx={{ position: "absolute", left: 16, color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        {/* Image */}
        <Box
          component="img"
          src={listingData.images.slice(0, 10)[lightboxIndex]}
          alt={`Image ${lightboxIndex + 1}`}
          onClick={(e) => e.stopPropagation()}
          sx={{ maxHeight: "90vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 1 }}
        />

        {/* Next arrow */}
        {lightboxIndex < Math.min(listingData.images.length, 10) - 1 && (
          <IconButton
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
            sx={{ position: "absolute", right: 16, color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}

        {/* Counter */}
        <Typography sx={{ position: "absolute", bottom: 16, color: "white", fontSize: "0.85rem" }}>
          {lightboxIndex + 1} / {Math.min(listingData.images.length, 10)}
        </Typography>
      </Box>
    )}
    </>
  );
};

export default AnalyzeAirbnbModalDashboard;
